import React, { ReactElement, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { createInitPagination, renderPaginationFooter } from 'app/shared/pagination/pagination.util';
import { sortByColumnWithoutUpdateUtil } from 'app/shared/util/table-util';
import { Alert, Table } from 'reactstrap';
import ViewTableHeaderColumn, { IViewTableHeaderColumnProps } from 'app/shared/view-table/view-table-header-column';
import { isArray } from 'app/shared/util/common';
import { Pagination } from 'edozvola-frontend-lib/dist/ui';
import { Loader } from 'app/shared/loader/loader';
import { UseQueryResult } from '@tanstack/react-query';
import './view-table.scss';
import { createPortal } from 'react-dom';

type QueryResult<T> = UseQueryResult<T, unknown>;
type FetchFunction = (pagination: PaginationState, ...args: any[]) => QueryResult<any>;

interface IServerManagedData {
  initialPaginationColumn: string;
  initialSortDirection?: string;
  fetchDataQuery: FetchFunction;
  fetchDataProps?: {};
  localStorageKey?: string;
  tableData?: never;
  dataLength?: never;
  pagination?: never;
  onPageChange?: never;
  onSizeChange?: never;
}

interface IFrontendManagedData {
  initialPaginationColumn?: never;
  initialSortDirection?: string;
  fetchDataQuery?: never;
  fetchDataProps?: never;
  localStorageKey?: never;
  tableData: any;
  dataLength: number;
  pagination?: PaginationState;
  onPageChange?: any;
  onSizeChange?: any;
}

interface IAdditionalProps {
  renderTableRow: Function;
  children: ReactElement<typeof ViewTableHeaderColumn> | ReactElement<typeof ViewTableHeaderColumn>[];
  noDataMessage?: string;
  striped?: boolean;
  keepStaleWhileLoading?: boolean;
  initialSortingDirection?: string;
  responsive?: boolean;
  autoLayout?: boolean;
  stickyHeader?: boolean;
  renderTableActions?: (dataLength: number, pagination: PaginationState) => ReactNode;
  tableActionParent?: Element | DocumentFragment;
}

export type IViewTable = IAdditionalProps & (IServerManagedData | IFrontendManagedData);

const ViewTable: React.FC<IViewTable> = ({
                                           fetchDataQuery,
                                           fetchDataProps,
                                           tableData,
                                           dataLength,
                                           initialPaginationColumn,
                                           initialSortDirection,
                                           renderTableRow,
                                           noDataMessage,
                                           pagination: propPagination,
                                           onPageChange: propOnPageChange,
                                           onSizeChange: propOnSizeChange,
                                           children,
                                           initialSortingDirection,
                                           striped = true,
                                           keepStaleWhileLoading = true,
                                           responsive = true,
                                           localStorageKey,
                                           autoLayout,
                                           stickyHeader = false,
                                           renderTableActions,
                                           tableActionParent,
                                         }) => {

  const loadPaginationFromLocalStorage = () => {
    let statePagination = { ...pagination };
    const page = localStorage.getItem(`${localStorageKey}-page`);
    const size = localStorage.getItem(`${localStorageKey}-size`);
    statePagination = {
      ...statePagination,
      page: page != null ? Number.parseInt(page) : statePagination.page,
      size: size != null ? Number.parseInt(size) : statePagination.size,
    };
    setPagination(statePagination);
  };

  const [pagination, setPagination] = useState<PaginationState>(
    propPagination ? propPagination : createInitPagination(initialPaginationColumn, initialSortDirection),
  );
  const [tempData, setTempData] = useState({ content: [], totalElements: 0 });
  const firstRender = useRef(true);

  const { data, isLoading } = fetchDataQuery
    ? fetchDataProps
      ? fetchDataQuery(pagination, fetchDataProps)
      : fetchDataQuery(pagination)
    : {
      data: {
        content: tableData,
        totalElements: dataLength,
      },
      isLoading: false,
    };

  useLayoutEffect(() => {
    if (localStorageKey != null && !propPagination) {
      loadPaginationFromLocalStorage();
    }
  }, []);

  useLayoutEffect(() => {
    if (propPagination && !firstRender.current) {
      setPagination(propPagination);
    }
  }, [propPagination]);

  useEffect(() => {
    if (!firstRender.current) {
      setPagination(s => ({ ...s, page: 1 }));
      if (localStorageKey) {
        localStorage.setItem(`${localStorageKey}-page`, '1');
      }
    }
    firstRender.current = false;
  }, [fetchDataProps]);

  const onPageChange = (page: number) => {
    if (keepStaleWhileLoading) {
      setTempData(data);
    }
    if (propOnPageChange) {
      propOnPageChange(page);
    }
    setPagination(s => ({ ...s, page }));
    if (localStorageKey) {
      localStorage.setItem(`${localStorageKey}-page`, page.toString());
    }
  };
  const onSizeChange = (size: number) => {
    if (keepStaleWhileLoading) {
      setTempData(data);
    }
    if (propOnSizeChange) {
      propOnSizeChange(size);
    }
    setPagination(s => ({ ...s, page: 1, size }));
    if (localStorageKey) {
      localStorage.setItem(`${localStorageKey}-size`, size.toString());
      localStorage.setItem(`${localStorageKey}-page`, '1');
    }
  };
  const sortByColumn = (event: React.MouseEvent<HTMLDivElement>) => {
    if (keepStaleWhileLoading) {
      setTempData(data);
    }
    sortByColumnWithoutUpdateUtil(event, pagination, setPagination);
  };

  const totalElements = data?.content ? data.totalElements : tempData.totalElements;

  return (
    <>
      {(isLoading || (!data?.content && !tempData?.content)) && <Loader />}
      {renderTableActions && (
        <div className="d-flex justify-content-end mb-2">
          {tableActionParent
            ? createPortal(renderTableActions(data?.content?.length, pagination), tableActionParent)
            : renderTableActions(data?.content?.length, pagination)}
        </div>
      )}
      {!isLoading && !data?.content?.length && noDataMessage ? (
        <Alert color="info" className="uniform-width">
          {noDataMessage}
        </Alert>
      ) : (
        <>
          {(data?.content?.length ?? 0) > 0 || (keepStaleWhileLoading && (tempData?.content?.length ?? 0) > 0) ? (
            <>
              <Table
                size={'sm'}
                bordered={true}
                responsive={responsive}
                striped={striped}
                style={{ tableLayout: autoLayout ? 'auto' : 'fixed' }}
              >
                <thead className={stickyHeader ? 'sticky-header' : ''}>
                  <tr>
                    {isArray(children) &&
                      children.map((child, index) => (
                        <React.Fragment key={index}>
                          {React.isValidElement(child)
                            ? React.cloneElement(child, {
                                pagination: pagination,
                                sortFunction: sortByColumn,
                              } as IViewTableHeaderColumnProps)
                            : null}
                        </React.Fragment>
                      ))}
                    {!isArray(children) && (
                      <React.Fragment>
                        {React.isValidElement(children)
                          ? React.cloneElement(children, {
                              pagination: pagination,
                              sortFunction: sortByColumn,
                            } as IViewTableHeaderColumnProps)
                          : null}
                      </React.Fragment>
                    )}
                  </tr>
                </thead>
                {data?.content?.length && (
                  <tbody>{data && data.content?.map((value, index) => renderTableRow(value, index, data.totalElements))}</tbody>
                )}
                {!data?.content?.length && isLoading && (
                  <tbody>{tempData && tempData.content?.map((value, index) => renderTableRow(value, index, tempData.totalElements))}</tbody>
                )}
              </Table>
              <div className="row">
                <div className="col-lg-6 d-flex justify-content-start">
                  {totalElements > 10 ? (
                    <Pagination
                      page={pagination.page}
                      size={pagination.size}
                      total={totalElements}
                      onChange={isLoading || !data?.content ? () => {} : onPageChange}
                      onSizeChange={isLoading || !data?.content ? () => {} : onSizeChange}
                      sizeOptions={[10, 25, 50]}
                      className="float-left"
                    />
                  ) : undefined}
                </div>
                <div className="col-lg-6 text-end">
                  {data?.content && <h6>{renderPaginationFooter({ ...pagination, total: totalElements })}</h6>}
                </div>
              </div>
            </>
          ) : undefined}
        </>
      )}
    </>
  );
};

export default ViewTable;
